import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';

import useFlexibleChart from './hook';

import { styles } from './index.styles';

const FlexibleChartJsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { data, loading, errorMsg, refetch, noData } = useStatistics();
  const ariaId = useId();

  const {
    chartConfig,
    chartType,
    chartTypeSupported,
    ChartComponent,
    chartData,
    chartOptions,
    error: chartError,
  } = useFlexibleChart({
    config,
    data,
    noData,
  });

  const content = useMemo(() => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles.container,
          }}
        >
          <Skeleton
            data-testid="skeleton"
            variant="rectangular"
            width="100%"
            height="100%"
          />
        </Box>
      );
    }

    if (!chartConfig) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles.container,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      );
    }

    if (!chartTypeSupported) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles.container,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Unsupported chart type: {chartType}
          </Typography>
        </Box>
      );
    }

    if (!chartData) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...styles.container,
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          ...styles.container,
        }}
      >
        {ChartComponent && (
          <ChartComponent data={chartData} options={chartOptions} />
        )}
      </Box>
    );
  }, [
    ChartComponent,
    chartConfig,
    chartData,
    chartOptions,
    chartType,
    chartTypeSupported,
    loading,
  ]);

  const error = errorMsg || chartError || undefined;

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={error}
      loading={loading}
      minHeight="auto"
      maxHeight="none"
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
    >
      {content}
    </WidgetFrame>
  );
};

export default FlexibleChartJsWidget;
